<script>
/* eslint-env browser */
/* global FB */
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { mapGetters, mapActions } from 'vuex';
import ChannelApi from '../../../../../../../../api/channels';
import { useAccount } from 'dashboard/composables/useAccount';

export default {
  components: {
    LoadingState,
  },
  props: {
    onSubmit: {
      type: [Function],
      default: () => {},
    },
  },
  setup() {
    const { accountId } = useAccount();
    return { accountId };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('ASSISTANT_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
      loginCompleted: false,
    };
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    ...mapGetters({
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
    }),
  },

  created() {
    this.initFB();
    this.loadFBsdk();
  },

  mounted() {
    this.initFB();
  },

  methods: {
    ...mapActions('assistantWizard', ['next', 'updateChannelConfigItems']),

    startLogin(e) {
      if (e) e.preventDefault();

      this.hasLoginStarted = true;
      this.tryFBlogin();
    },

    setPageName({ name }) {
      // Save to store
      this.pageName = name;
      this.updateChannelConfigItems({
        channel: 'facebook_page',
        items: this.channelParams(),
      });
    },

    initFB() {
      if (window.fbSDKLoaded === undefined) {
        window.fbAsyncInit = () => {
          FB.init({
            appId: window.rookooConfig.fbAppId,
            xfbml: true,
            version: window.rookooConfig.fbApiVersion,
            status: true,
          });
          window.fbSDKLoaded = true;
          FB.AppEvents.logPageView();
        };
      }
    },

    loadFBsdk() {
      ((d, s, id) => {
        let js;
        // eslint-disable-next-line
        const fjs = (js = d.getElementsByTagName(s)[0]);
        if (d.getElementById(id)) {
          return;
        }
        js = d.createElement(s);
        js.id = id;
        js.src = '//connect.facebook.net/en_US/sdk.js';
        fjs.parentNode.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          this.loginCompleted = true;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is logged into Facebook, but not your app.
            this.errorStateMessage = this.$t(
              'ASSISTANT_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'ASSISTANT_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            this.errorStateMessage = this.$t(
              'ASSISTANT_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          config_id: this.globalConfig.facebookLoginForBusinessConfigId,
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;
      } catch (error) {
        // Ignore error
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
      };
    },
  },
};
</script>

<template>
  <section class="flex items-center flex-col">
    <div
      v-if="!hasLoginStarted"
      class="login-init flex justify-center flex-col items-center"
    >
      <button class="flex max-w-lg items-center" @click="startLogin()">
        <img
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook icon with the text: Continue with Facebook"
          width="320px"
          class="self-center"
        />
      </button>
      <p>
        {{
          useInstallationName(
            $t('ASSISTANT_MGMT.ADD.FB.HELP'),
            globalConfig.installationName
          )
        }}
      </p>
    </div>
    <div v-else>
      <form class="flex flex-col w-full flex-1 mb-4" @submit.prevent="next()">
        <label
          class="text-2xl text-slate-900 dark:text-slate-50 mb-2"
          for="page"
        >
          {{ $t('ASSISTANT_MGMT.WIZARD.CHANNELS.FACEBOOK.PAGE_SELECT') }}
        </label>

        <section v-if="loginCompleted">
          <multiselect
            v-model.trim="selectedPage"
            :close-on-select="true"
            :allow-empty="true"
            :options="getSelectablePages"
            track-by="id"
            label="name"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
            :placeholder="$t('ASSISTANT_MGMT.ADD.FB.PICK_A_VALUE')"
            selected-label
            @select="setPageName"
          />
        </section>
        <section v-else>
          <p>
            {{ $t('ASSISTANT_MGMT.WIZARD.CHANNELS.FACEBOOK.WAITING_FOR_AUTH') }}
          </p>
        </section>
      </form>
    </div>
  </section>
</template>

<style scoped lang="scss">
.login-init {
  @apply text-center;

  p {
    @apply p-6;
  }

  > a > img {
    @apply w-60;
  }
}
</style>
